// Shared helpers for SEO meta tags and Schema.org JSON-LD.
// Prices are always derived from the same data the page renders, so the
// structured data can never drift from the visible content.

export const BASE_URL = 'https://deinserverhost.com';
export const DEFAULT_IMAGE = '/images/penguin/server_penguin_500.png';

const BRAND = { '@type': 'Brand', name: 'DeinServerHost' };
const UNIT_CODES = { month: 'MON', year: 'ANN' };

export const absoluteUrl = (path) => (path.startsWith('http') ? path : `${BASE_URL}${path}`);

// '19,95' | '19.95' | 19.95 → '19.95'
export const formatPrice = (price) => Number(String(price).replace(',', '.')).toFixed(2);

// ['19,95', '1.95'] → '1.95'
export const lowestPrice = (prices) => formatPrice(Math.min(...prices.map(formatPrice)));

/**
 * Product schema with one Offer per plan.
 *
 * @param {object}   p
 * @param {string}   p.name
 * @param {string}   p.description
 * @param {string}   p.path    – canonical path of the page
 * @param {string}   [p.image] – path or absolute URL
 * @param {object[]} p.offers  – [{ name?, price, period?: 'month'|'year', unitText?, soldOut? }]
 */
export function productSchema({ name, description, path, image = DEFAULT_IMAGE, offers }) {
  const toOffer = ({ name: offerName, price, period, unitText, soldOut }) => {
    const amount = formatPrice(price);
    return {
      '@type': 'Offer',
      ...(offerName && { name: offerName }),
      price: amount,
      priceCurrency: 'EUR',
      availability: soldOut ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
      url: absoluteUrl(path),
      ...(period && {
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: amount,
          priceCurrency: 'EUR',
          referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitCode: UNIT_CODES[period] },
          ...(unitText && { unitText }),
        },
      }),
    };
  };

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    image: absoluteUrl(image),
    brand: BRAND,
    offers: offers.length === 1 ? toOffer(offers[0]) : offers.map(toOffer),
  };
}

/**
 * BreadcrumbList starting at the home page.
 * @param {{ name: string, path: string }[]} items – trail below "Home"
 */
export function breadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [{ name: 'Home', path: '/' }, ...items].map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}
